/**
 * Ce qu'on achemine, en face des heures de transport.
 *
 * *Demande de Chris, 16/09/2026 : « rajoute les lignes des matériaux carrières du groupe avec le
 * tonnage dans la cellule, ce qui visuellement permettra de comprendre ce qu'on achemine en face
 * des heures de transport ; idéalement la ligne juste au-dessus des bennes, et tu répartis le
 * tonnage total en fonction de l'ensemble qui transporte. »*
 *
 * Le planning des ressources disait jusqu'ici combien d'heures une benne avait sur les bras, sans
 * jamais dire ce qu'elle allait charger. Un responsable transport lisait « 3,5 h » sans savoir
 * s'il s'agissait de 40 ou de 240 tonnes.
 *
 * ## Le rattachement se fait par l'OUVRAGE, parce que c'est le seul lien qui existe
 *
 * Vérifié sur la production le 16/09/2026 (APO250026, ouvrage 4 « REPROFILAGE DE PISTE ») : le
 * matériau du groupe et ses moyens — porteur 8x4, tracteur 4x2 + benne acier, chauffeurs — vivent
 * dans le **même ouvrage** de la contre-étude. C'est là, et nulle part ailleurs, que le produit
 * sait qu'un tonnage et des camions vont ensemble.
 *
 * **Et il n'y a rien de plus fin.** Le même ouvrage porte deux articles du groupe et deux
 * attelages, sans que rien ne dise lequel porte lequel — c'est la décision du 03/09/2026 :
 * *aucun appariement prévisionnel entre une rotation et son chargement, le lien naît de la
 * réalisation*. Répartir « en fonction de l'ensemble qui transporte », comme Chris l'a demandé,
 * est donc la seule répartition que les données permettent : à parts égales entre les véhicules
 * de l'ouvrage, ce jour-là.
 *
 * ## Ce qui est écarté, et pourquoi
 *
 * Entrent les matériaux que **nos camions vont chercher** : ceux des carrières du groupe, et
 * ceux qu'un fournisseur vend mais qu'on enlève **par nos soins**.
 *
 * *Les seconds ont été ajoutés le 16/09/2026, sur un cas relevé par Chris : le 23/09 d'APO260128,
 * un porteur 8x4 affichait 4 h sans rien en face. L'ouvrage 3.1 portait bien deux livraisons ce
 * jour-là, mais de fournisseurs — dont un béton de fondation en mode « interne », c'est-à-dire
 * que c'était bien nous qui allions le chercher. Une rotation muette là où l'on sait ce que le
 * camion charge est exactement ce que cette ligne existe pour empêcher.*
 *
 * **Reste écarté ce qu'un fournisseur livre lui-même** : aucun de nos véhicules ne bouge, il n'y
 * a pas de part à répartir. C'est la seule chose que la provenance change ici — et l'écran nomme
 * toujours d'où vient le matériau, pour qu'une carrière et un achat ne se lisent pas pareil.
 *
 * **Et les ARTICLES en sont exclus** — *« je ne t'ai pas parlé de faire apparaître les autres
 * articles que les matériaux »* (Chris, 16/09/2026). Les deux portent `duGroupe`, et tous deux
 * désignent une carrière : sur la production, le 0/31,5 concassé, la réduction à joints Ø 110 et
 * la barrière Heras en sont tous. Rien ne les sépare que le **préfixe du code** — la règle du
 * 20/08/2026, paramétrée par installation (`prefixe_article`, `100` chez ROY), que le serveur
 * pose sur chaque besoin.
 *
 * *Un premier essai filtrait sur l'unité, écartant ce qui n'est pas convertible en masse. Il
 * donnait le bon résultat sur les données du jour — les articles y sont en `U`, les matériaux en
 * `T` — mais par coïncidence : un matériau vendu à l'unité, ou un article facturé à la tonne,
 * l'aurait pris en défaut sans que rien ne le signale.*
 *
 * ## La quantité vient de la PASTILLE, jamais du contrôle
 *
 * `controle.tonnage` est un contrôle **de ligne**, recopié sur chacune de ses pastilles : une
 * ligne livrée en deux échéances le porte deux fois, et le sommer compterait le tonnage double.
 * C'est `quantite` — la part de cette échéance-là — qui est la bonne grandeur.
 */
import { dec } from "@planif/moteur";
import type { BesoinDate } from "./api";

/** Un matériau à acheminer sur un ouvrage : ce qu'on charge, et combien, jour par jour. */
export type LigneMateriau = {
  /** `chantierId#poste#code` — unique, et stable d'un rafraîchissement à l'autre. */
  readonly cle: string;
  readonly chantierId: string;
  readonly apo: string;
  readonly poste: string;
  readonly posteLibelle: string;
  readonly code: string;
  readonly libelle: string;
  readonly unite: string;
  /** D'où il vient : la carrière du groupe, ou le fournisseur qu'on enlève par nos soins. */
  readonly provenance: string;
  /** La quantité de chaque jour, indexée par date. Un jour sans livraison n'y figure pas. */
  readonly jours: ReadonlyMap<string, string>;
  /** La quantité de toute la période affichée. */
  readonly total: string;
  /**
   * Vrai quand aucun véhicule du groupe n'est affecté à cet ouvrage sur la période.
   *
   * La ligne reste affichée — c'est précisément le cas qu'il faut voir : du tonnage à acheminer
   * et personne pour le charger. La taire ferait d'un manque une absence de ligne, qui ne se
   * remarque pas.
   */
  readonly sansVehicule: boolean;
};

/** Ce qu'un véhicule charge, sur un jour donné. */
export type PartDuVehicule = {
  readonly tonnes: string;
  readonly unite: string;
};

/** « Par nos soins » : c'est un de nos véhicules qui va le chercher. */
const PAR_NOS_SOINS = "interne";

function estMateriauAAcheminer(besoin: BesoinDate): boolean {
  if (besoin.nature !== "livraison" || besoin.quantite === null) return false;
  // Un article n'est pas un matériau, et c'est le serveur qui le sait : le préfixe du code est un
  // paramètre d'installation, pas une forme que l'écran pourrait reconnaître.
  if (besoin.estArticle) return false;
  // Du groupe, ou acheté dehors mais enlevé par nos moyens : dans les deux cas un camion roule.
  return besoin.duGroupe || besoin.modeLivraison === PAR_NOS_SOINS;
}

/**
 * D'où vient ce matériau, en deux mots — la carrière, ou le fournisseur qu'on va enlever.
 *
 * Nommée plutôt que déduite d'une couleur : depuis que les achats « par nos soins » entrent dans
 * la liste, une ligne de carrière et une ligne de fournisseur s'y ressemblent, et seule cette
 * mention les distingue.
 */
export function provenanceDuMateriau(besoin: BesoinDate): string {
  if (besoin.duGroupe) {
    return besoin.carriereCode === null ? "du groupe" : `carrière ${besoin.carriereCode}`;
  }
  const fournisseur = besoin.fournisseur?.trim();
  return fournisseur === undefined || fournisseur === ""
    ? "par nos soins"
    : `par nos soins · ${fournisseur}`;
}

/** La clé d'un ouvrage : un tonnage et des camions ne vont ensemble que dans le même. */
function cleOuvrage(besoin: BesoinDate): string {
  return `${besoin.chantierId}#${besoin.poste}`;
}

/**
 * Les véhicules du groupe affectés à un ouvrage, un jour donné.
 *
 * Les **machines seulement**, et non les chauffeurs : « l'ensemble qui transporte » est un parc
 * de bennes, et compter le chauffeur avec son camion diviserait le tonnage par deux pour un seul
 * véhicule sur la route. Un même véhicule affecté deux fois le même jour sur l'ouvrage ne compte
 * qu'une fois — c'est un ensemble, pas une somme d'affectations.
 */
export function vehiculesParOuvrageEtJour(
  besoins: readonly BesoinDate[],
): ReadonlyMap<string, ReadonlySet<string>> {
  const parOuvrage = new Map<string, Set<string>>();
  for (const besoin of besoins) {
    if (besoin.nature !== "transport" || !besoin.estMateriel) continue;
    const cle = `${cleOuvrage(besoin)}#${besoin.date}`;
    const vehicules = parOuvrage.get(cle) ?? new Set<string>();
    parOuvrage.set(cle, vehicules);
    for (const affectation of besoin.affectations) {
      if (affectation.nature === "materiel") vehicules.add(affectation.ressourceId);
    }
  }
  return parOuvrage;
}

/**
 * Les lignes de matériau à afficher, une par ouvrage et par article.
 *
 * Par ARTICLE et non par ouvrage : deux matériaux d'un même ouvrage ne se confondent pas en une
 * quantité unique — « 240 T » sans dire de quoi n'aiguille personne. C'est aussi ce qui garantit
 * qu'une ligne ne porte qu'une seule unité.
 */
export function lignesDeMateriau(
  besoins: readonly BesoinDate[],
  apoParChantier: ReadonlyMap<string, string>,
): readonly LigneMateriau[] {
  const vehicules = vehiculesParOuvrageEtJour(besoins);
  const parCle = new Map<
    string,
    { modele: BesoinDate; jours: Map<string, string>; total: ReturnType<typeof dec> }
  >();

  for (const besoin of besoins) {
    if (!estMateriauAAcheminer(besoin)) continue;
    const cle = `${cleOuvrage(besoin)}#${besoin.code}`;
    const entree = parCle.get(cle) ?? {
      modele: besoin,
      jours: new Map<string, string>(),
      total: dec(0),
    };
    parCle.set(cle, entree);
    const dejaPose = entree.jours.get(besoin.date);
    const cumul = dec(dejaPose ?? "0").add(dec(besoin.quantite as string));
    entree.jours.set(besoin.date, cumul.toFixed());
    entree.total = entree.total.add(dec(besoin.quantite as string));
  }

  const lignes: LigneMateriau[] = [];
  for (const [cle, { modele, jours, total }] of parCle) {
    const porte = [...jours.keys()].some(
      (date) => (vehicules.get(`${cleOuvrage(modele)}#${date}`)?.size ?? 0) > 0,
    );
    lignes.push({
      cle,
      chantierId: modele.chantierId,
      apo: apoParChantier.get(modele.chantierId) ?? modele.chantierId,
      poste: modele.poste,
      posteLibelle: modele.posteLibelle,
      code: modele.code,
      libelle: modele.libelle,
      unite: modele.unite,
      provenance: provenanceDuMateriau(modele),
      jours,
      total: total.toFixed(),
      sansVehicule: !porte,
    });
  }

  /*
    L'ordre de lecture du responsable : son affaire, puis l'ouvrage dans l'ordre du devis, puis
    l'article. `localeCompare` sur le poste garde « 4 » avant « 10 » tant que les numéros sont
    écrits pareil, et c'est le même tri que partout ailleurs dans le produit.
  */
  return lignes.sort(
    (a, b) =>
      a.apo.localeCompare(b.apo, "fr") ||
      a.poste.localeCompare(b.poste, "fr", { numeric: true }) ||
      a.code.localeCompare(b.code, "fr"),
  );
}

/**
 * Ce que chaque véhicule charge, indexé `ressourceId#date`.
 *
 * Le tonnage de l'ouvrage divisé par le nombre de véhicules qui y sont affectés ce jour-là. Un
 * ouvrage qui porte deux matériaux de même unité les additionne : c'est bien la charge totale que
 * l'ensemble doit déplacer.
 *
 * **Les unités différentes ne s'additionnent pas.** Un ouvrage mêlant des tonnes et un autre
 * étalon rendrait une part par unité ; en pratique la production n'en connaît qu'une, mais le
 * jour où elle en connaîtra deux, personne ne lira « 60 » sans savoir de quoi.
 */
export function partParVehicule(
  besoins: readonly BesoinDate[],
): ReadonlyMap<string, readonly PartDuVehicule[]> {
  const vehicules = vehiculesParOuvrageEtJour(besoins);

  /** `ouvrage#date#unite` → quantité à acheminer. */
  const aCharger = new Map<string, ReturnType<typeof dec>>();
  for (const besoin of besoins) {
    if (!estMateriauAAcheminer(besoin)) continue;
    const cle = `${cleOuvrage(besoin)}#${besoin.date}#${besoin.unite}`;
    aCharger.set(cle, (aCharger.get(cle) ?? dec(0)).add(dec(besoin.quantite as string)));
  }

  const parts = new Map<string, PartDuVehicule[]>();
  for (const [cle, quantite] of aCharger) {
    const separateur = cle.lastIndexOf("#");
    const unite = cle.slice(separateur + 1);
    const ouvrageEtJour = cle.slice(0, separateur);
    const date = ouvrageEtJour.slice(ouvrageEtJour.lastIndexOf("#") + 1);
    const surLeJour = vehicules.get(ouvrageEtJour);
    if (surLeJour === undefined || surLeJour.size === 0) continue;
    const part = quantite.div(surLeJour.size).toFixed();
    for (const vehicule of surLeJour) {
      const cleVehicule = `${vehicule}#${date}`;
      const liste = parts.get(cleVehicule) ?? [];
      parts.set(cleVehicule, liste);
      /*
        Un véhicule pris sur deux ouvrages le même jour cumule ses charges au lieu de n'en garder
        qu'une : il les fera toutes les deux, et n'afficher que la dernière lue ferait disparaître
        du tonnage réel de l'écran.
      */
      const dejaLa = liste.findIndex((p) => p.unite === unite);
      const cumulable = dejaLa < 0 ? undefined : liste[dejaLa];
      if (cumulable === undefined) liste.push({ tonnes: part, unite });
      else liste[dejaLa] = { tonnes: dec(cumulable.tonnes).add(dec(part)).toFixed(), unite };
    }
  }
  return parts;
}
